
import { AnimatePresence, motion } from 'framer-motion';
import { Activity, BookOpen, Cpu, Layers, Microscope, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';


import { buildLightingConfig, getRenderingProfile } from './RenderingProfiles.js';

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM — Adaptation par profil
// ═══════════════════════════════════════════════════════════════════════════════

const DESIGN_SYSTEM = {
  colors: {
    // Backgrounds
    bg: '#080c14',
    bgPanel: '#0d1220',
    bgCard: '#111827',
    bgCardHover: '#161e30',
    bgInset: '#0a0e1a',
    
    // Grid & Borders
    grid: 'rgba(0, 229, 255, 0.04)',
    border: 'rgba(0, 229, 255, 0.10)',
    borderHover: 'rgba(0, 229, 255, 0.25)',
    borderActive: 'rgba(0, 229, 255, 0.55)',
    
    // Accents
    cyan: '#00e5ff',
    cyanDim: '#007a99',
    cyanFaint: 'rgba(0, 229, 255, 0.08)',
    green: '#7fff6e',
    greenDim: '#3a7a33',
    greenFaint: 'rgba(127, 255, 110, 0.08)',
    amber: '#ffb340',
    amberFaint: 'rgba(255, 179, 64, 0.10)',
    red: '#ff4d6d',
    redFaint: 'rgba(255, 77, 109, 0.10)',
    violet: '#a78bfa',
    violetFaint: 'rgba(167, 139, 250, 0.10)',
    
    // Text
    textPrimary: '#e2ecff',
    textSecond: '#7a8db5',
    textMuted: '#cad0db',
    textAccent: '#00e5ff',
    
    // 3D
    threeAccent: '#00ccee',
    threeGlow: '#003344',
  },
  fonts: {
    mono: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
    display: '"Syne", "Space Grotesk", system-ui, sans-serif',
    body: '"Inter", system-ui, sans-serif',
  },
};

const C = DESIGN_SYSTEM.colors;

const getDesignSystem = (profile) => {
  const theme = profile.theme;
  return {
    colors: {
      bg: theme.bgPrimary,
      bgPanel: theme.bgSecondary,
      bgCard: theme.bgSecondary,
      bgCardHover: theme.bgSecondary,
      bgInset: theme.bgPrimary,
      
      grid: theme.grid,
      border: theme.border,
      borderHover: theme.border.replace('0.12', '0.25'),
      borderActive: theme.border.replace('0.12', '0.55'),
      
      cyan: theme.accentPrimary,
      cyanDim: theme.accentSecondary,
      cyanFaint: theme.accentPrimary + '18',
      green: theme.accentTertiary || theme.accentPrimary,
      greenDim: theme.accentSecondary,
      greenFaint: theme.accentSecondary + '10',
      amber: theme.accentSecondary,
      amberFaint: theme.accentSecondary + '18',
      red: '#ff4d6d',
      redFaint: 'rgba(255, 77, 109, 0.10)',
      violet: '#a78bfa',
      violetFaint: 'rgba(167, 139, 250, 0.10)',
      
      textPrimary: theme.textPrimary,
      textSecond: theme.textSecondary,
      textMuted: theme.textSecondary,
      textAccent: theme.accentPrimary,
      
      threeAccent: theme.accentPrimary,
      threeGlow: theme.bgSecondary,
    },
    fonts: {
      mono: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
      display: '"Syne", "Space Grotesk", system-ui, sans-serif',
      body: '"Inter", system-ui, sans-serif',
    },
  };
};

export const CRTShader = {
  uniforms: {
    tDiffuse: { value: null },
    intensity: { value: 0.08 },
    scanlineFrequency: { value: 1.2 },
    time: { value: 0 },
    resolution: { value: new THREE.Vector2(1920, 1080) },
  },

  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float intensity;
    uniform float scanlineFrequency;
    uniform float time;
    uniform vec2 resolution;
    varying vec2 vUv;

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Scanlines (optimisé: une seule onde sinusoïdale)
      float scanline = sin(vUv.y * resolution.y * scanlineFrequency) * 0.5 + 0.5;
      scanline = mix(1.0, scanline, intensity * 0.5);
      
      // Vibration subtile (glow CRT)
      float vibration = sin(time * 0.3) * 0.001;
      vec2 uv = vUv + vec2(vibration, 0.0);
      
      // Re-sample avec vibration
      vec4 vibrated = texture2D(tDiffuse, uv);
      
      // Blend et appliquer scanline
      vec4 finalColor = mix(color, vibrated, 0.1);
      finalColor.rgb *= scanline;
      finalColor.rgb += color.rgb * 0.02 * intensity; // Phosphor glow subtil
      
      gl_FragColor = finalColor;
    }
  `,
};

// ═══════════════════════════════════════════════════════════════════════════════
// SHADER: COLOR GRADING (Léger) — Saturation, Brightness, Contrast
// ═══════════════════════════════════════════════════════════════════════════════

export const ColorGradingShader = {
  uniforms: {
    tDiffuse: { value: null },
    saturation: { value: 1.0 },
    brightness: { value: 1.0 },
    contrast: { value: 1.0 },
  },

  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float saturation;
    uniform float brightness;
    uniform float contrast;
    varying vec2 vUv;

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Brightness (simple multiplication)
      color.rgb *= brightness;
      
      // Contrast (center + scale)
      color.rgb = (color.rgb - 0.5) * contrast + 0.5;
      
      // Saturation (mélange avec greyscale)
      vec3 gray = vec3(dot(color.rgb, vec3(0.299, 0.587, 0.114)));
      color.rgb = mix(gray, color.rgb, saturation);
      
      gl_FragColor = clamp(color, 0.0, 1.0);
    }
  `,
};

// ═══════════════════════════════════════════════════════════════════════════════
// SHADER: DISTORSION (Optimisé) — Force field effect
// ═══════════════════════════════════════════════════════════════════════════════

export const DistortionShader = {
  uniforms: {
    tDiffuse: { value: null },
    intensity: { value: 0.04 },
    frequency: { value: 0.3 },
    time: { value: 0 },
  },

  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float intensity;
    uniform float frequency;
    uniform float time;
    varying vec2 vUv;

    // Cheap noise function (optimisé pour perfs)
    float cheapNoise(vec2 p) {
      return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
    }

    void main() {
      vec2 uv = vUv;
      
      // Distorsion ondulatoire simple
      float distort = sin(uv.y * frequency * 20.0 + time) * intensity;
      distort += cos(uv.x * frequency * 15.0 + time * 0.8) * intensity * 0.6;
      
      uv.x += distort;
      uv.y += distort * 0.5;
      
      // Clamp pour éviter les répétitions
      uv = clamp(uv, 0.001, 0.999);
      
      vec4 color = texture2D(tDiffuse, uv);
      gl_FragColor = color;
    }
  `,
};

// ═══════════════════════════════════════════════════════════════════════════════
// SHADER: VIGNETTE AVANCÉE (Modulaire)
// ═══════════════════════════════════════════════════════════════════════════════

export const AdvancedVignetteShader = {
  uniforms: {
    tDiffuse: { value: null },
    strength: { value: 0.1 },
    falloff: { value: 2.0 },
  },

  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float strength;
    uniform float falloff;
    varying vec2 vUv;

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Distance du centre
      vec2 center = vUv - 0.5;
      float dist = length(center);
      
      // Vignette circulaire smooth
      float vignette = 1.0 - smoothstep(0.0, falloff, dist * falloff);
      vignette = mix(1.0, vignette, strength);
      
      color.rgb *= vignette;
      
      gl_FragColor = color;
    }
  `,
};


// Fonction pour convertir en toute sécurité
const color = (color, defaultColor = '#000000') => {
  if (color === undefined || color === null || color === 0) {
    return new THREE.Color(defaultColor);
  }
  
  // Si c'est un nombre, le convertir en string hexadécimale
  if (typeof color === 'number') {
    return new THREE.Color('#' + color.toString(16).padStart(6, '0'));
  }
  
  return new THREE.Color(color);
};



const PARAM_SPECS = {
  pression_arterielle: { lo: 60, hi: 90, unit: 'mmHg', label: 'PRESSION' },
  debit_cardiaque: { lo: 50, hi: 85, unit: '%', label: 'DÉBIT CARD.' },
  filtration: { lo: 60, hi: 100, unit: 'mL', label: 'FILTRATION' },
  fréquence_cardiaque: { lo: 55, hi: 100, unit: 'bpm', label: 'FC' },
  résistance_périphérique: { lo: 40, hi: 75, unit: '%', label: 'RVP' },
  équilibre_global: { lo: 50, hi: 90, unit: '%', label: 'ÉQUILIBRE' },
  stress_oxydatif: { lo: 0, hi: 50, unit: '%', label: 'ROS' },
  volémie: { lo: 55, hi: 85, unit: '%', label: 'VOLÉMIE' },
  gestion_risque: { lo: 60, hi: 90, unit: '%', label: 'RISQUE' },
  discipline_executive: { lo: 65, hi: 90, unit: '%', label: 'DISCIPLINE' },
  lecture_marche: { lo: 50, hi: 80, unit: '%', label: 'LECTURE' },
  gestion_biais: { lo: 55, hi: 85, unit: '%', label: 'BIAIS' },
  resilience: { lo: 50, hi: 85, unit: '%', label: 'RÉSILIENCE' },
  performance_durable: { lo: 55, hi: 85, unit: '%', label: 'PERF DUR.' },
  clarte_decisionnelle: { lo: 50, hi: 85, unit: '%', label: 'CLARTÉ' },
  pression_marche: { lo: 0, hi: 100, unit: '%', label: 'PRESSION MKT' },
  volatilite: { lo: 0, hi: 100, unit: '%', label: 'VOLATILITÉ' },
  liquidite: { lo: 0, hi: 100, unit: '%', label: 'LIQUIDITÉ' },
  engagement: { lo: 50, hi: 90, unit: '%', label: 'ENGAGEMENT' },
  comprehension: { lo: 50, hi: 90, unit: '%', label: 'COMPRÉHENSION' },
  memorisation: { lo: 40, hi: 85, unit: '%', label: 'MÉMOIRE' },
  

};

const INFERENCE_CONFIG = {
  channel: 'pesacore_inference',
  staleThreshold: 8000,
  pollIntervalActive: 200,
  pollIntervalInactive: 2000,
  fontId: 'pesa-fonts-v1',
  fontUrl: 'https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700&family=JetBrains+Mono:wght@300;400;500&family=Inter:wght@300;400;500&display=swap',
};

const useInferenceEngine = (activeId) => {
  const [payload, setPayload] = useState(null);
  const [status, setStatus] = useState('waiting');
  const [latency, setLatency] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const lastRawRef = useRef(null);
  const bcRef = useRef(null);

  const parseAndValidate = useCallback((raw) => {
    if (!raw || raw === lastRawRef.current) return null;
    lastRawRef.current = raw;

    const t0 = performance.now();
    try {
      const parsed = JSON.parse(typeof raw === 'string' ? raw : JSON.stringify(raw));
      const dt = performance.now() - t0;

      const viz = extractVisualization(parsed, 0, activeId);
      const hasContent = viz || parsed?.state || parsed?.case || parsed?.scenario;
      
      if (!hasContent) {
        setErrorMsg('Payload reçu sans contenu reconnu');
        setStatus('error');
        return null;
      }

      if (activeId) {
        const vizKey = `pesa_viz_${activeId}`;
        if (!parsed.visualization3D || typeof parsed.visualization3D !== 'object') {
          try {
            const stored = localStorage.getItem(vizKey);
            if (stored) parsed.visualization3D = JSON.parse(stored);
          } catch (e) {}
        }
      }

      setLatency(Math.round(dt * 10) / 10);
      setLastUpdate(Date.now());
      setStatus('connected');
      setErrorMsg(null);
      return parsed;
    } catch (e) {
      setErrorMsg(`Parse error: ${e.message}`);
      setStatus('error');
      return null;
    }
  }, [activeId]);

  useEffect(() => {
    // BroadcastChannel (préféré)
    try {
      bcRef.current = new BroadcastChannel(INFERENCE_CONFIG.channel);
      bcRef.current.onmessage = (e) => {
        const parsed = parseAndValidate(
          typeof e.data === 'string' ? e.data : JSON.stringify(e.data)
        );
        if (parsed) setPayload(parsed);
      };
    } catch (_) {
      // Fallback si BroadcastChannel non supporté
    }

    // localStorage avec polling adaptatif
    const getLocalKeys = () => {
      const candidates = [];
      if (activeId) candidates.push(`pesa_sync_${activeId}`);
      Object.keys(localStorage)
        .filter(k => k.startsWith('pesa_sync_') && !candidates.includes(k))
        .forEach(k => candidates.push(k));
      return candidates;
    };

    const pollLocal = () => {
      for (const key of getLocalKeys()) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = parseAndValidate(raw);
        if (parsed) {
          setPayload(parsed);
          return;
        }
      }
    };

    pollLocal();

    // Storage event listener
    const onStorage = (e) => {
      if (!e.key?.startsWith('pesa_sync_')) return;
      const parsed = parseAndValidate(e.newValue);
      if (parsed) setPayload(parsed);
    };
    window.addEventListener('storage', onStorage);

    // Polling de secours avec staleness check
    let pollInterval = INFERENCE_CONFIG.pollIntervalInactive;
    const pollTimer = setInterval(() => {
      pollLocal();
      if (lastUpdate && Date.now() - lastUpdate > INFERENCE_CONFIG.staleThreshold) {
        setStatus('stale');
      } else if (lastUpdate) {
        pollInterval = INFERENCE_CONFIG.pollIntervalActive;
      }
    }, pollInterval);

    return () => {
      bcRef.current?.close();
      window.removeEventListener('storage', onStorage);
      clearInterval(pollTimer);
    };
  }, [activeId, parseAndValidate]);

  return { payload, status, latency, lastUpdate, errorMsg };
};

const useParameterHistory = (parameters, maxHistory = 24) => {
  const histRef = useRef({});

  useEffect(() => {
    Object.entries(parameters).forEach(([key, value]) => {
      if (!histRef.current[key]) histRef.current[key] = [];
      histRef.current[key].push(value ?? 0);
      if (histRef.current[key].length > maxHistory) {
        histRef.current[key].shift();
      }
    });
  }, [JSON.stringify(parameters)]);

  return histRef;
};

const SignalBar = ({ status, latency }) => {
  const C = DESIGN_SYSTEM.colors;
  const MONO = DESIGN_SYSTEM.fonts.mono;
  
  const bars = [0.3, 0.5, 0.7, 0.9, 1.1, 0.8, 0.4, 0.6, 1.0, 0.7];
  const colors = {
    connected: C.green,
    stale: C.amber,
    error: C.red,
    waiting: C.textMuted,
  };
  const color = colors[status] || C.textMuted;
  const isActive = status === 'connected';

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-end gap-[2px] h-4">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            style={{ width: 2, background: color, borderRadius: 1 }}
            animate={
              isActive
                ? { height: `${h * 16}px`, opacity: [0.4, 1, 0.4] }
                : { height: '2px', opacity: 0.25 }
            }
            transition={
              isActive
                ? { duration: 0.8, repeat: Infinity, delay: i * 0.08, ease: 'easeInOut' }
                : { duration: 0 }
            }
          />
        ))}
      </div>
      <span style={{ fontFamily: MONO, fontSize: 9, color, letterSpacing: '0.12em' }}>
        {status === 'connected' && `LIVE${latency ? ` · ${latency}ms` : ''}`}
        {status === 'stale' && 'STALE'}
        {status === 'error' && 'ERROR'}
        {status === 'waiting' && 'WAITING'}
      </span>
    </div>
  );
};

const StatusChip = ({ value, lo, hi, unit }) => {
  const C = DESIGN_SYSTEM.colors;
  const MONO = DESIGN_SYSTEM.fonts.mono;
  
  const color = value < lo ? C.red : value > hi ? C.amber : C.green;
  return (
    <span
      style={{
        fontFamily: MONO,
        fontSize: 9,
        color,
        background: color + '18',
        border: `1px solid ${color}35`,
        borderRadius: 3,
        padding: '1px 5px',
      }}
    >
      {Math.round(value)}{unit}
    </span>
  );
};

const Micro = ({ values = [], color = C.cyan, w = 52, h = 18 }) => {
  if (values.length < 2) return <div style={{ width: w, height: h }} />;
  const mn = Math.min(...values);
  const mx = Math.max(...values);
  const range = mx - mn || 1;
  const pts = values
    .map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - mn) / range) * (h - 2) - 1}`)
    .join(' ');
  const last = values[values.length - 1];
  const lx = w;
  const ly = h - ((last - mn) / range) * (h - 2) - 1;
  return (
    <svg width={w} height={h} style={{ overflow: 'visible', flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1" strokeOpacity="0.6" />
      <circle cx={lx} cy={ly} r="1.5" fill={color} />
    </svg>
  );
};

const ParameterMonitor = ({ parameters }) => {
  const C = DESIGN_SYSTEM.colors;
  const MONO = DESIGN_SYSTEM.fonts.mono;
  
  const histRef = useParameterHistory(parameters);
  const keys = Object.keys(parameters).slice(0, 8);

  if (!keys.length) {
    return (
      <div
        className="flex items-center justify-center py-8"
        style={{ color: C.textMuted, fontFamily: MONO, fontSize: 9 }}
      >
        — AUCUN PARAMÈTRE —
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {keys.map(k => {
        const v = parameters[k] ?? 0;
        const meta = PARAM_SPECS[k] || { lo: 40, hi: 80, unit: '', label: k.slice(0, 8).toUpperCase() };
        const color = v < meta.lo ? C.red : v > meta.hi ? C.amber : C.green;
        const hist = histRef.current[k] || [v];
        const delta = hist.length > 1 ? v - hist[hist.length - 2] : 0;

        return (
          <div
            key={k}
            style={{
              background: C.bgInset,
              border: `1px solid ${C.border}`,
              borderLeft: `2px solid ${color}`,
              borderRadius: 4,
              padding: '6px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <div
              style={{
                fontFamily: MONO,
                fontSize: 8,
                color: C.textMuted,
                minWidth: 56,
                letterSpacing: '0.08em',
              }}
            >
              {meta.label}
            </div>
            <Micro values={hist} color={color} />
            <div style={{ flex: 1 }}>
              <div style={{ height: 2, background: 'rgba(255,255,255,0.04)', borderRadius: 1 }}>
                <motion.div
                  animate={{ width: `${Math.min(100, (v / (meta.hi * 1.2)) * 100)}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  style={{ height: '100%', background: color, borderRadius: 1 }}
                />
              </div>
            </div>
            <StatusChip value={v} lo={meta.lo} hi={meta.hi} unit={meta.unit} />
            {Math.abs(delta) > 0.5 && (
              <span style={{ fontFamily: MONO, fontSize: 9, color: delta > 0 ? C.green : C.red }}>
                {delta > 0 ? '↑' : '↓'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const LayersPanel = ({ layers = [], layerOpacities, setLayerOpacities }) => {
  const C = DESIGN_SYSTEM.colors;
  const MONO = DESIGN_SYSTEM.fonts.mono;
  const DISPLAY = DESIGN_SYSTEM.fonts.display;
  const BODY = DESIGN_SYSTEM.fonts.body;
  const [collapsed, setCollapsed] = useState({});

  const groups = useMemo(() => {
    const map = {};
    layers.forEach(l => {
      const g = l.group || 'Structures';
      if (!map[g]) map[g] = [];
      map[g].push(l);
    });
    return map;
  }, [layers]);

  if (!layers.length) return null;

  return (
    <div className="space-y-1">
      {Object.entries(groups).map(([gName, gLayers]) => {
        const isCol = !!collapsed[gName];
        const visCount = gLayers.filter(l => (layerOpacities[l.id] ?? l.opacity ?? 1) > 0.03).length;

        return (
          <div key={gName} style={{ border: `1px solid ${C.border}`, borderRadius: 4, overflow: 'hidden' }}>
            <button
              onClick={() => setCollapsed(p => ({ ...p, [gName]: !p[gName] }))}
              className="w-full flex items-center justify-between px-3 py-2 transition-colors"
              style={{ background: isCol ? 'transparent' : C.bgCard }}
            >
              <div className="flex items-center gap-2">
                <span style={{ fontFamily: MONO, fontSize: 8, color: C.textAccent, letterSpacing: '0.15em' }}>
                  {gName.toUpperCase()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted }}>
                  {visCount}/{gLayers.length}
                </span>
                <motion.span animate={{ rotate: isCol ? -90 : 0 }} style={{ color: C.textMuted, fontSize: 8 }}>
                  ▾
                </motion.span>
              </div>
            </button>

            <AnimatePresence>
              {!isCol && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div style={{ padding: '4px 8px 8px' }}>
                    {gLayers.map(layer => {
                      const op = layerOpacities[layer.id] ?? layer.opacity ?? 1;
                      const vis = op > 0.03;
                      const col = layer.material?.color || C.cyan;
                      return (
                        <div
                          key={layer.id}
                          className="flex items-center gap-2 py-1.5 px-2 rounded"
                          style={{
                            background: vis ? col + '0a' : 'transparent',
                            border: `1px solid ${vis ? col + '25' : 'transparent'}`,
                            marginBottom: 2,
                          }}
                        >
                          <button
                            onClick={() =>
                              setLayerOpacities(p => ({
                                ...p,
                                [layer.id]: vis ? 0 : layer.opacity ?? 1,
                              }))
                            }
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: vis ? col : 'transparent',
                              border: `1px solid ${col}`,
                              flexShrink: 0,
                              cursor: 'pointer',
                              boxShadow: vis ? `0 0 4px ${col}60` : 'none',
                            }}
                          />
                          <span
                            style={{
                              fontFamily: MONO,
                              fontSize: 10,
                              color: vis ? C.textPrimary : C.textMuted,
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {layer.label || layer.id}
                          </span>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={op}
                            onChange={e =>
                              setLayerOpacities(p => ({
                                ...p,
                                [layer.id]: parseFloat(e.target.value),
                              }))
                            }
                            style={{ width: 36, accentColor: col, cursor: 'pointer' }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

const InfoCard = ({ inter, layer, obj, parameters, onClose }) => {
  const C = DESIGN_SYSTEM.colors;
  const MONO = DESIGN_SYSTEM.fonts.mono;
  const DISPLAY = DESIGN_SYSTEM.fonts.display;
  const BODY = DESIGN_SYSTEM.fonts.body;
  const [tab, setTab] = useState('info');
  if (!inter) return null;

  const meta = layer || obj || {};
  const accent = obj?.color || layer?.material?.color || C.cyan;

  const paramKey = (inter.onClick?.showParameter || '').replace('show_parameter:', '').trim();
  const paramVal = paramKey ? parameters[paramKey] ?? null : null;
  const paramMeta = paramKey ? PARAM_SPECS[paramKey] : null;
  const paramColor =
    paramVal == null ? C.cyan : paramVal < (paramMeta?.lo ?? 40) ? C.red : paramVal > (paramMeta?.hi ?? 80) ? C.amber : C.green;

  const tabs = [
    { id: 'info', label: 'INFO' },
    meta.pathologyNotes && { id: 'path', label: 'PATHO' },
    (inter.theoryRef || inter.onClick?.theoryRef) && { id: 'theory', label: 'THÉORIE' },
  ].filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      style={{
        background: C.bgCard,
        border: `1px solid ${accent}40`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: `linear-gradient(90deg, ${accent}, transparent)` }} />

      <div style={{ padding: '10px 12px 8px' }}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 3 }}>
              POINT D'INTÉRÊT
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 14, color: C.textPrimary, fontWeight: 600 }}>
              {inter.label || inter.id}
            </div>
            {meta.role && (
              <div style={{ fontFamily: MONO, fontSize: 9, color: accent, marginTop: 2 }}>{meta.role}</div>
            )}
          </div>
          <button onClick={onClose} style={{ color: C.textMuted, cursor: 'pointer', flexShrink: 0, paddingTop: 2 }}>
            <X size={12} />
          </button>
        </div>
      </div>

      {paramKey && paramVal !== null && (
        <div style={{ padding: '6px 12px', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between">
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted }}>
              {paramKey.replace(/_/g, ' ').toUpperCase()}
            </span>
            <motion.span
              key={Math.round(paramVal ?? 0)}
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              style={{ fontFamily: MONO, fontSize: 13, color: paramColor, fontWeight: 700 }}
            >
              {Math.round(paramVal ?? 0)}
            </motion.span>
          </div>
          <div style={{ height: 3, background: 'rgba(255,255,255,0.05)', borderRadius: 2, marginTop: 4 }}>
            <motion.div
              animate={{ width: `${Math.min(100, ((paramVal ?? 0) / (paramMeta?.hi ?? 100) ?? 0) * 100)}%` }}
              transition={{ duration: 0.5 }}
              style={{ height: '100%', background: paramColor, borderRadius: 2 }}
            />
          </div>
        </div>
      )}

      {tabs.length > 1 && (
        <div className="flex" style={{ borderBottom: `1px solid ${C.border}` }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flex: 1,
                padding: '6px 0',
                fontFamily: MONO,
                fontSize: 8,
                letterSpacing: '0.1em',
                color: tab === t.id ? C.cyan : C.textMuted,
                borderBottom: `1px solid ${tab === t.id ? C.cyan : 'transparent'}`,
                background: tab === t.id ? C.cyanFaint : 'transparent',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ padding: '10px 12px' }}>
        <AnimatePresence mode="wait">
          {tab === 'info' && (
            <motion.div key="info" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {inter.description && (
                <p style={{ fontFamily: BODY, fontSize: 12, color: C.textSecond, lineHeight: 1.7, margin: 0 }}>
                  {inter.description}
                </p>
              )}
              {meta.clinicalRole && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    background: accent + '08',
                    border: `1px solid ${accent}20`,
                    borderRadius: 4,
                  }}
                >
                  <div
                    style={{
                      fontFamily: MONO,
                      fontSize: 7,
                      color: C.textMuted,
                      letterSpacing: '0.12em',
                      marginBottom: 4,
                    }}
                  >
                    RÔLE CLINIQUE
                  </div>
                  <p style={{ fontFamily: BODY, fontSize: 11, color: C.textPrimary, lineHeight: 1.6, margin: 0 }}>
                    {meta.clinicalRole}
                  </p>
                </div>
              )}
            </motion.div>
          )}
          {tab === 'path' && meta.pathologyNotes && (
            <motion.div key="path" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div
                style={{
                  padding: 8,
                  background: C.redFaint,
                  border: `1px solid ${C.red}30`,
                  borderRadius: 4,
                }}
              >
                <div style={{ fontFamily: MONO, fontSize: 7, color: C.red, letterSpacing: '0.12em', marginBottom: 4 }}>
                  IMPACT PATHOLOGIQUE
                </div>
                <p style={{ fontFamily: BODY, fontSize: 11, color: '#cc7070', lineHeight: 1.6, margin: 0 }}>
                  {meta.pathologyNotes}
                </p>
              </div>
            </motion.div>
          )}
          {tab === 'theory' && (
            <motion.div key="theory" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div
                style={{
                  padding: 8,
                  background: C.violetFaint,
                  border: `1px solid ${C.violet}30`,
                  borderRadius: 4,
                }}
              >
                <div style={{ fontFamily: MONO, fontSize: 7, color: C.violet, letterSpacing: '0.12em', marginBottom: 4 }}>
                  CADRE THÉORIQUE
                </div>
                <p style={{ fontFamily: BODY, fontSize: 11, color: '#b0a0d8', lineHeight: 1.6, margin: 0 }}>
                  {(inter.theoryRef || inter.onClick?.theoryRef || '').replace(/_/g, ' ')}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const StepPanel = ({ step, stepIndex, totalSteps }) => {
  const C = DESIGN_SYSTEM.colors;
  const MONO = DESIGN_SYSTEM.fonts.mono;
  const DISPLAY = DESIGN_SYSTEM.fonts.display;
  const BODY = DESIGN_SYSTEM.fonts.body;
  if (!step) return null;
  const progress = ((stepIndex + 1) / (totalSteps || 1)) * 100;

  return (
    <div
      style={{
        background: C.bgCard,
        border: `1px solid ${C.border}`,
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 2, background: 'rgba(255,255,255,0.05)' }}>
        <motion.div
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{ height: '100%', background: `linear-gradient(90deg, ${C.cyanDim}, ${C.cyan})` }}
        />
      </div>
      <div style={{ padding: '8px 12px' }}>
        <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 4 }}>
          ÉTAPE {String(stepIndex + 1).padStart(2, '0')} / {totalSteps || '—'}
        </div>
        <div style={{ fontFamily: DISPLAY, fontSize: 13, color: C.textPrimary, fontWeight: 600, lineHeight: 1.35 }}>
          {step.title}
        </div>
        {step.epistemologicalQuestion && (
          <div
            style={{
              fontFamily: BODY,
              fontSize: 11,
              color: C.textMuted,
              marginTop: 5,
              lineHeight: 1.6,
              borderLeft: `2px solid ${C.cyanDim}`,
              paddingLeft: 8,
            }}
          >
            {step.epistemologicalQuestion}
          </div>
        )}
      </div>
    </div>
  );
};

const WaitingScreen = ({ status, errorMsg }) => {
  const C = DESIGN_SYSTEM.colors;
const MONO = DESIGN_SYSTEM.fonts.mono;
const DISPLAY = DESIGN_SYSTEM.fonts.display;
const BODY = DESIGN_SYSTEM.fonts.body;

return (
  <div className="fixed inset-0 flex flex-col items-center justify-center" style={{ background: C.bg }}>
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `linear-gradient(${C.grid} 1px, transparent 1px), linear-gradient(90deg, ${C.grid} 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background: 'radial-gradient(ellipse at center, transparent 35%, rgba(8,12,20,0.85) 100%)',
      }}
    />

    <div className="relative flex flex-col items-center gap-10">
      <div style={{ width: 200, height: 48, position: 'relative' }}>
        <svg width="200" height="48" style={{ display: 'block' }}>
          <line
            x1="0"
            y1="24"
            x2="200"
            y2="24"
            stroke={C.textMuted}
            strokeWidth="0.5"
            strokeDasharray="4 8"
            strokeOpacity="0.3"
          />
          {status === 'connected' ? (
            <motion.polyline
              points="0,24 20,24 30,8 40,36 50,24 70,24 80,10 90,34 100,24 120,24 130,12 140,32 150,24 170,24 180,8 190,36 200,24"
              fill="none"
              stroke={C.green}
              strokeWidth="1.5"
              strokeOpacity="0.9"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />
          ) : (
            <motion.polyline points="0,24 200,24" fill="none" stroke={C.textMuted} strokeWidth="1" strokeOpacity="0.3" />
          )}
        </svg>
      </div>

      <div style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: MONO, fontSize: 9, color: C.textAccent, letterSpacing: '0.3em', marginBottom: 8 }}>
          PESACORE Architect · MOTEUR 3D
        </div>
        <div style={{ fontFamily: DISPLAY, fontSize: 16, color: C.textSecond, fontWeight: 500, lineHeight: 1.5 }}>
          {status === 'error' ? 'Erreur de connexion' : 'En attente du flux d\'inférence…'}
        </div>
        {errorMsg && (
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.red, marginTop: 6 }}>{errorMsg}</div>
        )}
        {!errorMsg && (
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.textMuted, marginTop: 4 }}>
            Ouvrez le simulateur dans un autre onglet
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {[0, 0.25, 0.5].map((d, i) => (
          <motion.div
            key={i}
            style={{ width: 4, height: 4, borderRadius: '50%', background: C.textAccent }}
            animate={{ opacity: [0.15, 1, 0.15], scale: [0.7, 1.1, 0.7] }}
            transition={{ duration: 1.4, repeat: Infinity, delay: d, ease: 'easeInOut' }}
          />
        ))}
      </div>
    </div>

    <div
      className="absolute bottom-8"
      style={{
        fontFamily: MONO,
        fontSize: 10,
        color: C.textMuted + '30',
        letterSpacing: '0.25em',
      }}
    >
      LABORATORY · ANALYSIS · RESEARCH
    </div>
  </div>
);
};


const extractVisualization = (parsed, depth = 0, activeId = null) => {
  if (!parsed || typeof parsed !== 'object' || depth > 5) return null;
  if (parsed.type && (parsed.objects || parsed.layers)) return parsed;

  const direct = parsed.visualization3D || parsed.viz3d || parsed.visualization3d;
  if (direct && typeof direct === 'object') return direct;

  if (activeId) {
    try {
      const stored = localStorage.getItem(`pesa_viz_${activeId}`);
      if (stored) {
        const obj = JSON.parse(stored);
        if (obj?.type) return obj;
      }
    } catch (e) {}
  }

  for (const key of ['case', 'scenario', 'genericCase']) {
    const found = extractVisualization(parsed[key], depth + 1, activeId);
    if (found) return found;
  }
  return null;
};

const extractState = (parsed) =>
  parsed?.state || parsed?.case?.state || parsed?.scenario?.state || {};

const extractCase = (parsed) =>
  parsed?.case || parsed?.scenario || parsed?.genericCase || {};


// ═══════════════════════════════════════════════════════════════════════════════
// SCENE CREATION — Avec application du profil
// ═══════════════════════════════════════════════════════════════════════════════

const createBaseSceneWithProfile = (container, sceneType, camCfg = {}, ambiCfg = {}, savedCamera = null) => {
  const profile = getRenderingProfile(sceneType);
  

  if (!profile) {
    console.error(`Profil inconnu: ${sceneType}`);
    return null;
  }

  const W = container.clientWidth;
  const H = container.clientHeight;
  const scene = new THREE.Scene();  
  
  // Couleur de fond du profil
  const bgColor = color(profile.theme?.bgPrimary, '#0a0e16');
  scene.background = bgColor;
  if (profile.fog?.enabled) {
    const fogColor = color(profile.fog.color, '#0a0e16');
    scene.fog = new THREE.Fog(fogColor, profile.fog.near, profile.fog.far);
  } else {
    scene.fog = new THREE.Fog(bgColor, 15, 40); // Utiliser bgColor si fog désactivé
  }
  

  // Camera
  const camera = new THREE.PerspectiveCamera(
    profile.camera.fov,
    W / H,
    0.05,
    200
  );
  if (savedCamera?.position) {
    camera.position.copy(savedCamera.position);
  } else {
    camera.position.set(
      camCfg.initialPosition?.x ?? 0,
      camCfg.initialPosition?.y ?? 2,
      camCfg.initialPosition?.z ?? 8
    );
  }

  // Renderer avec haute qualité
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
    precision: 'highp',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(W, H);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = profile.postprocessing.toneMappingExposure;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setClearColor(profile.theme?.bgPrimary || '#0a0e16', 1.0);
  container.appendChild(renderer.domElement);

  // ── POST-PROCESSING ───────────────────────────────────────────────────────
  const composer = new EffectComposer(renderer);
    const w = renderer.domElement.clientWidth;
    const h = renderer.domElement.clientHeight;
  
    // 1. RENDER PASS
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);
  
    // 2. BLOOM PASS (adapté au profil)
    const ppCfg = profile.postprocessing;
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(w, h),
      ppCfg.bloomStrength,
      ppCfg.bloomRadius,
      ppCfg.bloomThreshold,
    );
    composer.addPass(bloomPass);
  
    // 3. COLOR GRADING PASS
    const colorGradingPass = new ShaderPass(ColorGradingShader);
    colorGradingPass.uniforms.saturation.value = ppCfg.colorCorrectionSaturation;
    colorGradingPass.uniforms.brightness.value = ppCfg.colorCorrectionBrightness;
    colorGradingPass.uniforms.contrast.value = 1.0;
    composer.addPass(colorGradingPass);
  
    // 4. CRT PASS (si activé)
    let crtPass = null;
    if (ppCfg.crtEffect?.enabled) {
      crtPass = new ShaderPass(CRTShader);
      crtPass.uniforms.intensity.value = ppCfg.crtEffect.intensity;
      crtPass.uniforms.scanlineFrequency.value = ppCfg.crtEffect.scanlineFrequency;
      crtPass.uniforms.resolution.value.set(w, h);
      composer.addPass(crtPass);
    }
  
    // 5. DISTORSION PASS (si activé)
    let distortionPass = null;
    if (ppCfg.distortion?.enabled) {
      distortionPass = new ShaderPass(DistortionShader);
      distortionPass.uniforms.intensity.value = ppCfg.distortion.intensity;
      distortionPass.uniforms.frequency.value = ppCfg.distortion.frequency;
      composer.addPass(distortionPass);
    }
  
    // 6. VIGNETTE PASS (si strength > 0)
    let vignettePass = null;
    if (ppCfg.vignetteStrength > 0) {
      vignettePass = new ShaderPass(AdvancedVignetteShader);
      vignettePass.uniforms.strength.value = ppCfg.vignetteStrength;
      vignettePass.uniforms.falloff.value = 1.5;
      composer.addPass(vignettePass);
    }
  
    // 7. FINAL COPY PASS
    const copyPass = new ShaderPass(CopyShader);
    copyPass.renderToScreen = true;
    composer.addPass(copyPass);

  // ── CONTRÔLES ────────────────────────────────────────────────────────────
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.065;
  controls.minDistance = profile.camera.minDistance;
  controls.maxDistance = profile.camera.maxDistance;
  controls.autoRotate = false;
  controls.enableZoom = true;
  if (savedCamera?.target) {
    controls.target.copy(savedCamera.target);
  } else {
    controls.target.set(
      camCfg.lookAt?.x ?? 0,
      camCfg.lookAt?.y ?? 0,
      camCfg.lookAt?.z ?? 0
    );
  }
  controls.update();

  // ── ÉCLAIRAGE DU PROFIL ───────────────────────────────────────────────────
  const lightingCfg = buildLightingConfig(profile);

  // Ambient soft light
  scene.add(new THREE.AmbientLight(
    lightingCfg.ambient.color,
    lightingCfg.ambient.intensity
  ));

  // Key light
  const keyLight = new THREE.DirectionalLight(
    lightingCfg.key.color,
    lightingCfg.key.intensity
  );
  keyLight.position.set(
    lightingCfg.key.position.x,
    lightingCfg.key.position.y,
    lightingCfg.key.position.z
  );
  keyLight.castShadow = true;
  keyLight.shadow.mapSize.width = 2048;
  keyLight.shadow.mapSize.height = 2048;
  keyLight.shadow.camera.far = 50;
  keyLight.shadow.camera.near = 0.5;
  keyLight.shadow.bias = -0.0002;
  scene.add(keyLight);

  // Rim light
  const rimLight = new THREE.DirectionalLight(
    lightingCfg.rim.color,
    lightingCfg.rim.intensity
  );
  rimLight.position.set(
    lightingCfg.rim.position.x,
    lightingCfg.rim.position.y,
    lightingCfg.rim.position.z
  );
  scene.add(rimLight);

  // Fill light
  const fillLight = new THREE.DirectionalLight(
    lightingCfg.fill.color,
    lightingCfg.fill.intensity
  );
  fillLight.position.set(
    lightingCfg.fill.position.x,
    lightingCfg.fill.position.y,
    lightingCfg.fill.position.z
  );
  scene.add(fillLight);

  // Back light (si intensité > 0)
  let backLight = null;
  if (lightingCfg.back.intensity > 0 && lightingCfg.back.position) {
    backLight = new THREE.DirectionalLight(
      lightingCfg.back.color,
      lightingCfg.back.intensity
    );
    backLight.position.set(
      lightingCfg.back.position.x ?? 0,
      lightingCfg.back.position.y ?? -5,
      lightingCfg.back.position.z ?? 5
    );
    scene.add(backLight);
  }

  // ── GRILLE SUBTILE ─────────────────────────────────────────────────────
  if (ambiCfg.grid !== false) {
    const gridHelper = new THREE.GridHelper(28, 28);
    gridHelper.material.color.set(profile.theme.accentPrimary);
    gridHelper.material.opacity = 0.1;
    gridHelper.material.transparent = true;
    gridHelper.position.y = -4.2;
    gridHelper.renderOrder = 0;
    scene.add(gridHelper);
  }

  // ── PARTICULES DE FOND ─────────────────────────────────────────────────
  const bgGeo = new THREE.BufferGeometry();
  const bgCount = Math.round(450 * profile.particlesIntensity);
  const bgPos = new Float32Array(bgCount * 3);
  for (let i = 0; i < bgCount * 3; i++) {
    bgPos[i] = (Math.random() - 0.5) * 32;
  }
  bgGeo.setAttribute('position', new THREE.BufferAttribute(bgPos, 3));
  const bgMat = new THREE.PointsMaterial({
    color: profile.theme.accentPrimary,
    size: 0.032,
    transparent: true,
    opacity: 0.3 * profile.particlesIntensity,
    depthWrite: false,
    sizeAttenuation: true,
  });
  scene.add(new THREE.Points(bgGeo, bgMat));

  const handleResize = () => {
    const nw = container.clientWidth;
    const nh = container.clientHeight;
    camera.aspect = nw / nh;
    camera.updateProjectionMatrix();
    renderer.setSize(nw, nh);
    composer.setSize(nw, nh);
  };
  window.addEventListener('resize', handleResize);

  return {
    scene,
    camera,
    renderer,
    controls,
    composer,
    keyLight,
    rimLight,
    fillLight,
    backLight,
    profile,
    getCameraState: () => ({
      position: camera.position.clone(),
      target: controls.target.clone(),
    }),
    dispose: () => {
      window.removeEventListener('resize', handleResize);
      controls.dispose();
      composer.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    },
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export const UnifiedSceneRenderer = ({
  config,
  parameters,
  state,
  hoveredTarget,
  setHoveredTarget,
  layerOpacities,
  onHotspotClick,
  sceneType,
}) => {
  const containerRef = useRef(null);
  const threeRef = useRef(null);
  const rafRef = useRef(null);
  const cameraRef = useRef(null);
  const layerGroupsRef = useRef({});
  const hotspotGroupsRef = useRef([]);
  const objectMeshesRef = useRef({});
  const linkMeshesRef = useRef([]);
  const particleSystemsRef = useRef({});
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const hoveredTargetRef = useRef(null);
  const postProcessingRef = useRef(null);
  const animationStateRef = useRef({
    time: 0,
    triggers: new Map(),
    dynamics: new Map(),
  });

  const paramsRef = useRef(parameters);
  const stateRef = useRef(state);
  const opacityRef = useRef(layerOpacities);
  const configRef = useRef(config);

  useEffect(() => {
    paramsRef.current = parameters;
  }, [parameters]);
  
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  
  useEffect(() => {
    opacityRef.current = layerOpacities;
  }, [layerOpacities]);
  
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Effet principal pour créer et gérer la scène
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !config) return;

    // Nettoyage de la scène précédente
    if (threeRef.current) {
      cameraRef.current = threeRef.current.getCameraState();
      threeRef.current.dispose();
      threeRef.current = null;
    }

    layerGroupsRef.current = {};
    hotspotGroupsRef.current = [];
    objectMeshesRef.current = {};
    linkMeshesRef.current = [];
    particleSystemsRef.current = {};
    animationStateRef.current.triggers.clear();
    animationStateRef.current.dynamics.clear();

    // Créer la scène de base avec le profil
    const sceneState = createBaseSceneWithProfile(
      container,
      sceneType || config.type,
      config.camera,
      config.ambiance,
      cameraRef.current
    );

    if (!sceneState) return;

    threeRef.current = sceneState;

    // ═══════════════════════════════════════════════════════════════════════════
    // SYSTÈME DE MATÉRIAUX AVANCÉS
    // ═══════════════════════════════════════════════════════════════════════════
    
    const createAdvancedMaterial = (config, type = 'standard') => {
      const baseColor = config?.color || '#00ccee';
      const opacity = config?.opacity ?? 0.9;
      
      switch(type) {
        case 'glass':
          return new THREE.MeshPhysicalMaterial({
            color: baseColor,
            metalness: 0.1,
            roughness: 0.1,
            transmission: 0.9,
            thickness: 1.5,
            transparent: true,
            opacity: opacity,
            envMapIntensity: 1.0,
            clearcoat: 1.0,
            clearcoatRoughness: 0.1,
          });
          
        case 'metal':
          return new THREE.MeshStandardMaterial({
            color: baseColor,
            metalness: 0.9,
            roughness: 0.1,
            transparent: true,
            opacity: opacity,
            envMapIntensity: 1.5,
          });
          
        case 'glow':
          return new THREE.MeshStandardMaterial({
            color: baseColor,
            metalness: 0.3,
            roughness: 0.4,
            emissive: baseColor,
            emissiveIntensity: 2.0,
            transparent: true,
            opacity: opacity,
          });
          
        case 'wireframe':
          return new THREE.MeshBasicMaterial({
            color: baseColor,
            wireframe: true,
            transparent: true,
            opacity: opacity * 0.7,
          });
          
        default:
          return new THREE.MeshStandardMaterial({
            color: baseColor,
            metalness: config?.metalness ?? 0.5,
            roughness: config?.roughness ?? 0.3,
            transparent: true,
            opacity: opacity,
            emissive: config?.emissive || '#000000',
            emissiveIntensity: config?.emissiveIntensity || 0,
          });
      }
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // CRÉATION DES VISUALISATIONS SPÉCIFIQUES
    // ═══════════════════════════════════════════════════════════════════════════
    
    // === VISUALISATION ANATOMIQUE : SYSTÈME CARDIAQUE 3D ===
    const createAnatomicalHeart = (config) => {
      const group = new THREE.Group();
      
      // Créer un cœur anatomique stylisé avec des courbes de Bézier
      const heartShape = new THREE.Shape();
      const x = 0, y = 0;
      heartShape.moveTo(x, y + 0.5);
      heartShape.bezierCurveTo(x, y + 1.5, x - 2, y + 1.5, x - 2, y);
      heartShape.bezierCurveTo(x - 2, y - 1, x - 1, y - 2, x, y - 1.5);
      heartShape.bezierCurveTo(x + 1, y - 2, x + 2, y - 1, x + 2, y);
      heartShape.bezierCurveTo(x + 2, y + 1.5, x, y + 1.5, x, y + 0.5);
      
      const extrudeSettings = {
        depth: 0.5,
        bevelEnabled: true,
        bevelSegments: 5,
        bevelSize: 0.1,
        bevelThickness: 0.1,
      };
      
      const heartGeometry = new THREE.ExtrudeGeometry(heartShape, extrudeSettings);
      const heartMaterial = createAdvancedMaterial({
        color: '#ff3355',
        metalness: 0.3,
        roughness: 0.4,
        emissive: '#ff0033',
        emissiveIntensity: 0.5,
      }, 'glow');
      
      const heart = new THREE.Mesh(heartGeometry, heartMaterial);
      heart.castShadow = true;
      heart.receiveShadow = true;
      group.add(heart);
      
      // Ajouter des vaisseaux sanguins (tubes)
      const createBloodVessel = (start, end, radius, color) => {
        const curve = new THREE.CatmullRomCurve3([
          new THREE.Vector3(...start),
          new THREE.Vector3(...end),
        ]);
        const tubeGeometry = new THREE.TubeGeometry(curve, 20, radius, 8, false);
        const tubeMaterial = createAdvancedMaterial({
          color: color,
          metalness: 0.2,
          roughness: 0.5,
        });
        const tube = new THREE.Mesh(tubeGeometry, tubeMaterial);
        tube.castShadow = true;
        tube.receiveShadow = true;
        return tube;
      };
      
      // Artères principales
      group.add(createBloodVessel([0, 1.5, 0.3], [0.5, 3, 0.5], 0.15, '#cc2244'));
      group.add(createBloodVessel([0, 1.5, 0.3], [-0.5, 3, 0.5], 0.15, '#cc2244'));
      group.add(createBloodVessel([0, 1.2, -0.3], [0, 2.5, -0.8], 0.12, '#dd3355'));
      
      // Veines
      group.add(createBloodVessel([-1.5, 0.5, 0], [-2.5, -0.5, 0.3], 0.1, '#4422aa'));
      group.add(createBloodVessel([1.5, 0.5, 0], [2.5, -0.5, 0.3], 0.1, '#4422aa'));
      
      // Valve cardiaque (anneau)
      const valveGeometry = new THREE.TorusGeometry(0.6, 0.05, 16, 32);
      const valveMaterial = createAdvancedMaterial({
        color: '#ffaa00',
        metalness: 0.8,
        roughness: 0.2,
        emissive: '#ff8800',
        emissiveIntensity: 0.3,
      }, 'metal');
      const valve = new THREE.Mesh(valveGeometry, valveMaterial);
      valve.position.z = 0.3;
      valve.rotation.x = Math.PI / 2;
      group.add(valve);
      
      // Animation de battement
      animationStateRef.current.dynamics.set(config.id || 'heart', {
        type: 'heart',
        heart: heart,
        baseScale: 1,
        beatSpeed: 1.2,
        beatAmplitude: 0.1,
      });
      
      return group;
    };
    
    // === VISUALISATION TRADING : TERMINAL BLOOMBERG 3D ===
    const createTradingDashboard = (config) => {
      const group = new THREE.Group();
      
      
      
      // Créer des graphiques de trading avancés
      const createCandlestickChart = (position, width, height) => {
        const chartGroup = new THREE.Group();
        const candles = [];
        
        // Créer 20 bougies
        for (let i = 0; i < 20; i++) {
          const open = Math.random() * height;
          const close = Math.random() * height;
          const high = Math.max(open, close) + Math.random() * 0.3;
          const low = Math.min(open, close) - Math.random() * 0.3;
          
          // Corps de la bougie
          const bodyGeometry = new THREE.BoxGeometry(0.15, Math.abs(close - open), 0.15);
          const isGreen = close > open;
          const bodyMaterial = new THREE.MeshStandardMaterial({
            color: isGreen ? '#00ff00' : '#ff0000',
            emissive: isGreen ? '#00ff00' : '#ff0000',
            emissiveIntensity: 0.5,
            metalness: 0.3,
            roughness: 0.4,
          });
          const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
          body.position.x = (i - 10) * 0.25;
          body.position.y = (open + close) / 2;
          chartGroup.add(body);
          
          // Mèche haute
          const wickTopGeometry = new THREE.CylinderGeometry(0.02, 0.02, high - Math.max(open, close), 4);
          const wickTop = new THREE.Mesh(wickTopGeometry, bodyMaterial);
          wickTop.position.x = (i - 10) * 0.25;
          wickTop.position.y = (high + Math.max(open, close)) / 2;
          chartGroup.add(wickTop);
          
          // Mèche basse
          const wickBottomGeometry = new THREE.CylinderGeometry(0.02, 0.02, Math.min(open, close) - low, 4);
          const wickBottom = new THREE.Mesh(wickBottomGeometry, bodyMaterial);
          wickBottom.position.x = (i - 10) * 0.25;
          wickBottom.position.y = (low + Math.min(open, close)) / 2;
          chartGroup.add(wickBottom);
          
          candles.push({ body, open, close, high, low });
        }
        
        chartGroup.position.set(position.x, position.y, position.z);
        return { group: chartGroup, candles };
      };
      
      // Créer le graphique principal
      const mainChart = createCandlestickChart({ x: 0, y: 1.5, z: 0 }, 5, 2);
      group.add(mainChart.group);
      
      // Créer des indicateurs techniques (lignes)
      const createIndicatorLine = (points, color) => {
        const curve = new THREE.CatmullRomCurve3(points);
        const curvePoints = curve.getPoints(50);
        const geometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
        const material = new THREE.LineBasicMaterial({ color, linewidth: 2 });
        const line = new THREE.Line(geometry, material);
        return line;
      };
      
      // Moyenne mobile
      const maPoints = [];
      for (let i = 0; i < 20; i++) {
        maPoints.push(new THREE.Vector3((i - 10) * 0.25, 1 + Math.sin(i * 0.3) * 0.3, 0.1));
      }
      group.add(createIndicatorLine(maPoints, '#ffaa00'));
      
      // Volume (barres en bas)
      for (let i = 0; i < 20; i++) {
        const volumeHeight = Math.random() * 0.5;
        const volumeGeometry = new THREE.BoxGeometry(0.15, volumeHeight, 0.15);
        const volumeMaterial = new THREE.MeshStandardMaterial({
          color: '#00ffff',
          emissive: '#00ffff',
          emissiveIntensity: 0.3,
          transparent: true,
          opacity: 0.6,
        });
        const volumeBar = new THREE.Mesh(volumeGeometry, volumeMaterial);
        volumeBar.position.set((i - 10) * 0.25, -0.5 + volumeHeight / 2, 0.1);
        group.add(volumeBar);
      }
      
      // Créer des tickers défilants (texte 3D simplifié avec des barres)
      const createTickerTape = () => {
        const tickerGroup = new THREE.Group();
        const tickerGeometry = new THREE.PlaneGeometry(5, 0.2);
        const tickerMaterial = new THREE.MeshStandardMaterial({
          color: '#000000',
          emissive: '#00ff00',
          emissiveIntensity: 0.8,
        });
        const ticker = new THREE.Mesh(tickerGeometry, tickerMaterial);
        ticker.position.y = 3.5;
        tickerGroup.add(ticker);
        
        // Ajouter des barres représentant les données
        for (let i = 0; i < 30; i++) {
          const barHeight = Math.random() * 0.15;
          const barGeometry = new THREE.BoxGeometry(0.02, barHeight, 0.02);
          const barMaterial = new THREE.MeshStandardMaterial({
            color: i % 3 === 0 ? '#ff0000' : '#00ff00',
            emissive: i % 3 === 0 ? '#ff0000' : '#00ff00',
            emissiveIntensity: 1,
          });
          const bar = new THREE.Mesh(barGeometry, barMaterial);
          bar.position.set((i - 15) * 0.17, 0.1, 0.01);
          tickerGroup.add(bar);
        }
        
        return tickerGroup;
      };
      
      group.add(createTickerTape());
      
      // Animation des bougies
      animationStateRef.current.dynamics.set('trading_dashboard', {
        type: 'trading',
        candles: mainChart.candles,
        updateSpeed: 0.5,
      });
      
      return group;
    };
    
    // === VISUALISATION SYSTÈME COMPLEXE : RÉSEAU DE FORCE ===
    const createForceFieldNetwork = (config) => {
      const group = new THREE.Group();
      
      // Créer des nœuds interconnectés
      const nodes = [];
      const nodeCount = config.nodes || 12;
      
      // Créer les nœuds en sphère
      for (let i = 0; i < nodeCount; i++) {
        const phi = Math.acos(1 - 2 * (i + 0.5) / nodeCount);
        const theta = Math.PI * (1 + Math.sqrt(5)) * i;
        
        const x = 2 * Math.sin(phi) * Math.cos(theta);
        const y = 2 * Math.sin(phi) * Math.sin(theta);
        const z = 2 * Math.cos(phi);
        
        const geometry = new THREE.SphereGeometry(0.15, 16, 16);
        const material = createAdvancedMaterial({
          color: '#00ffff',
          emissive: '#00ffff',
          emissiveIntensity: 1,
        }, 'glow');
        const node = new THREE.Mesh(geometry, material);
        node.position.set(x, y, z);
        group.add(node);
        
        // Ajouter un halo
        const haloGeometry = new THREE.SphereGeometry(0.25, 16, 16);
        const haloMaterial = new THREE.MeshBasicMaterial({
          color: '#00ffff',
          transparent: true,
          opacity: 0.3,
          blending: THREE.AdditiveBlending,
        });
        const halo = new THREE.Mesh(haloGeometry, haloMaterial);
        node.add(halo);
        
        nodes.push({ mesh: node, halo, originalPos: new THREE.Vector3(x, y, z) });
      }
      
      // Créer les connexions
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          if (Math.random() < 0.3) {
            const start = nodes[i].mesh.position;
            const end = nodes[j].mesh.position;
            const distance = start.distanceTo(end);
            
            if (distance < 3) {
              const curve = new THREE.CatmullRomCurve3([
                start.clone(),
                new THREE.Vector3(
                  (start.x + end.x) / 2,
                  (start.y + end.y) / 2 + 0.5,
                  (start.z + end.z) / 2
                ),
                end.clone(),
              ]);
              
              const tubeGeometry = new THREE.TubeGeometry(curve, 20, 0.02, 8, false);
              const tubeMaterial = new THREE.MeshBasicMaterial({
                color: '#00ffff',
                transparent: true,
                opacity: 0.2,
                blending: THREE.AdditiveBlending,
              });
              const tube = new THREE.Mesh(tubeGeometry, tubeMaterial);
              group.add(tube);
            }
          }
        }
      }
      
      // Animation du réseau
      animationStateRef.current.dynamics.set('force_field', {
        type: 'network',
        nodes: nodes,
      });
      
      return group;
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // CRÉATION DES OBJETS SELON LE TYPE DE SCÈNE
    // ═══════════════════════════════════════════════════════════════════════════
    // 
    const sceneTypeLower = (sceneType || config.type || 'anatomical').toLowerCase();
    
    if (sceneTypeLower === 'anatomical' || sceneTypeLower === 'medical' || sceneTypeLower === 'heart') {
      // Créer le système cardiaque
      const heartSystem = createAnatomicalHeart(config);
      heartSystem.position.set(0, 0, 0);
      sceneState.scene.add(heartSystem);
      objectMeshesRef.current['anatomical_heart'] = heartSystem;
      
      // Ajouter des couches anatomiques supplémentaires
      config.layers?.forEach(layer => {
        const layerGroup = new THREE.Group();
        
        if (layer.type === 'vascular' || layer.id.includes('vaisseau')) {
          // Créer un réseau vasculaire
          const mainCurve = new THREE.CatmullRomCurve3([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0.5, 1, 0.3),
            new THREE.Vector3(1, 2, 0.5),
            new THREE.Vector3(0.5, 3, 0.3),
          ]);
          
          const tubeGeometry = new THREE.TubeGeometry(mainCurve, 50, 0.08, 8, false);
          const tubeMaterial = createAdvancedMaterial({
            color: layer.color || '#cc2244',
            metalness: 0.2,
            roughness: 0.5,
            opacity: layerOpacities[layer.id] ?? layer.opacity ?? 1,
          });
          const mainVessel = new THREE.Mesh(tubeGeometry, tubeMaterial);
          layerGroup.add(mainVessel);
          
          // Branches
          for (let i = 0; i < 5; i++) {
            const branchCurve = new THREE.CatmullRomCurve3([
              new THREE.Vector3(0.5, 1 + i * 0.5, 0.3),
              new THREE.Vector3(1 + (i % 2 === 0 ? 0.5 : -0.5), 1.5 + i * 0.5, 0.5),
              new THREE.Vector3(1.5 + (i % 2 === 0 ? 0.5 : -0.5), 2 + i * 0.5, 0.7),
            ]);
            
            const branchGeometry = new THREE.TubeGeometry(branchCurve, 20, 0.04, 6, false);
            const branch = new THREE.Mesh(branchGeometry, tubeMaterial);
            layerGroup.add(branch);
          }
        } else if (layer.type === 'muscle' || layer.id.includes('muscle')) {
          // Couche musculaire
          const muscleGeometry = new THREE.SphereGeometry(1.5, 32, 32);
          const muscleMaterial = createAdvancedMaterial({
            color: '#cc3355',
            metalness: 0.1,
            roughness: 0.7,
            opacity: 0.3,
          });
          const muscle = new THREE.Mesh(muscleGeometry, muscleMaterial);
          layerGroup.add(muscle);
        } else if (layer.type === 'nerve' || layer.id.includes('nerf')) {
          // Réseau nerveux
          for (let i = 0; i < 8; i++) {
            const nerveCurve = new THREE.CatmullRomCurve3([
              new THREE.Vector3(0, 0, 0),
              new THREE.Vector3(Math.cos(i * Math.PI / 4) * 2, Math.sin(i * Math.PI / 4) * 2, 0),
              new THREE.Vector3(Math.cos(i * Math.PI / 4) * 3, Math.sin(i * Math.PI / 4) * 3, Math.sin(i) * 0.5),
            ]);
            
            const nerveGeometry = new THREE.TubeGeometry(nerveCurve, 30, 0.02, 4, false);
            const nerveMaterial = new THREE.MeshStandardMaterial({
              color: '#ffcc00',
              emissive: '#ffaa00',
              emissiveIntensity: 0.5,
              transparent: true,
              opacity: 0.7,
            });
            const nerve = new THREE.Mesh(nerveGeometry, nerveMaterial);
            layerGroup.add(nerve);
          }
        } else {
          // Layer générique
          const geometry = new THREE.SphereGeometry(layer.radius || 2, 32, 32);
          const material = createAdvancedMaterial({
            color: layer.color || '#ff5577',
            opacity: layerOpacities[layer.id] ?? layer.opacity ?? 0.7,
            metalness: 0.1,
            roughness: 0.4,
          });
          const mesh = new THREE.Mesh(geometry, material);
          layerGroup.add(mesh);
        }
        
        sceneState.scene.add(layerGroup);
        layerGroupsRef.current[layer.id] = layerGroup;
      });
      
    } else if (sceneTypeLower === 'trading' || sceneTypeLower === 'market' || sceneTypeLower === 'finance') {
      // Créer le dashboard de trading
      const tradingDashboard = createTradingDashboard(config);
      tradingDashboard.position.set(0, 0, 0);
      sceneState.scene.add(tradingDashboard);
      objectMeshesRef.current['trading_dashboard'] = tradingDashboard;
      
      // Ajouter des objets spécifiques du trading
      config.objects?.forEach(obj => {
        const objGroup = new THREE.Group();
        
        if (obj.type === 'lineChart' || obj.id === 'courbe_prix') {
          // Graphique en ligne sophistiqué
          const points = [];
          const dataPoints = obj.dataPoints || 60;
          
          for (let i = 0; i < dataPoints; i++) {
            const x = (i - dataPoints / 2) * 0.1;
            const y = Math.sin(i * 0.3) * 0.5 + Math.cos(i * 0.1) * 0.3;
            points.push(new THREE.Vector3(x, y, 0.1));
          }
          
          const curve = new THREE.CatmullRomCurve3(points);
          const curvePoints = curve.getPoints(100);
          const curveGeometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
          const curveMaterial = new THREE.LineBasicMaterial({
            color: obj.color || '#00ff00',
            linewidth: 2,
            transparent: true,
            opacity: obj.opacity || 0.9,
          });
          const line = new THREE.Line(curveGeometry, curveMaterial);
          objGroup.add(line);
          
          // Ajouter une surface sous la courbe
          const surfacePoints = [...curvePoints];
          surfacePoints.push(new THREE.Vector3(curvePoints[curvePoints.length - 1].x, -2, 0.1));
          surfacePoints.push(new THREE.Vector3(curvePoints[0].x, -2, 0.1));
          
          const surfaceGeometry = new THREE.BufferGeometry();
          const vertices = [];
          for (let i = 0; i < curvePoints.length; i++) {
            vertices.push(curvePoints[i].x, curvePoints[i].y, curvePoints[i].z);
            vertices.push(curvePoints[i].x, -2, curvePoints[i].z);
          }
          surfaceGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
          
          const surfaceMaterial = new THREE.MeshBasicMaterial({
            color: obj.color || '#00ff00',
            transparent: true,
            opacity: 0.1,
            side: THREE.DoubleSide,
          });
          const surface = new THREE.Mesh(surfaceGeometry, surfaceMaterial);
          objGroup.add(surface);
          
        } else if (obj.type === 'heatmap' || obj.id === 'volatilite_heatmap') {
          // Heatmap avancée
          const cols = 12;
          const rows = 10;
          
          for (let i = 0; i < cols; i++) {
            for (let j = 0; j < rows; j++) {
              const cellGeometry = new THREE.PlaneGeometry(0.2, 0.2);
              const intensity = Math.sin(i * 0.5 + j * 0.7) * 0.5 + 0.5;
              const cellMaterial = new THREE.MeshStandardMaterial({
                color: new THREE.Color().setHSL(0.7 - intensity * 0.7, 1, 0.5),
                emissive: new THREE.Color().setHSL(0.7 - intensity * 0.7, 1, 0.5),
                emissiveIntensity: intensity,
                transparent: true,
                opacity: 0.7,
              });
              const cell = new THREE.Mesh(cellGeometry, cellMaterial);
              cell.position.set((i - cols / 2) * 0.22, (j - rows / 2) * 0.22, 0.1);
              objGroup.add(cell);
            }
          }
          
        } else if (obj.type === 'particleFlow' || obj.id === 'flux_liquidite') {
          // Flux de particules avancé
          const particleCount = obj.particleCount || 200;
          const particleGeometry = new THREE.BufferGeometry();
          const positions = new Float32Array(particleCount * 3);
          const velocities = [];
          
          for (let i = 0; i < particleCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const radius = 1 + Math.random() * 2;
            positions[i * 3] = Math.cos(angle) * radius;
            positions[i * 3 + 1] = (Math.random() - 0.5) * 0.5;
            positions[i * 3 + 2] = Math.sin(angle) * radius;
            velocities.push({
              angle: angle,
              radius: radius,
              speed: 0.5 + Math.random() * 0.5,
              yOffset: Math.random() * Math.PI * 2,
            });
          }
          
          particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
          
          const particleMaterial = new THREE.PointsMaterial({
            color: obj.color || '#00ff88',
            size: 0.05,
            transparent: true,
            opacity: obj.opacity || 0.7,
            blending: THREE.AdditiveBlending,
          });
          
          const particles = new THREE.Points(particleGeometry, particleMaterial);
          objGroup.add(particles);
          
          animationStateRef.current.dynamics.set(obj.id, {
            type: 'particles',
            geometry: particleGeometry,
            positions,
            velocities,
          });
          
        } else if (obj.type === 'bar' || obj.id.includes('biais')) {
          // Barres d'indicateurs
          const value = obj.value || 50;
          const height = (value / 100) * 2;
          
          const barGeometry = new THREE.BoxGeometry(0.3, height, 0.3);
          const barMaterial = createAdvancedMaterial({
            color: obj.color || '#ff00ff',
            emissive: obj.color || '#ff00ff',
            emissiveIntensity: 0.5,
          }, 'glow');
          const bar = new THREE.Mesh(barGeometry, barMaterial);
          bar.position.y = height / 2;
          objGroup.add(bar);
          
          // Socle
          const baseGeometry = new THREE.BoxGeometry(0.4, 0.1, 0.4);
          const baseMaterial = new THREE.MeshStandardMaterial({
            color: '#ffffff',
            metalness: 0.8,
            roughness: 0.2,
          });
          const base = new THREE.Mesh(baseGeometry, baseMaterial);
          objGroup.add(base);
          
        } else if (obj.type === 'gauge' || obj.id === 'discipline_gauge') {
          // Jauge circulaire
          const gaugeGeometry = new THREE.TorusGeometry(0.5, 0.05, 16, 64);
          const gaugeMaterial = createAdvancedMaterial({
            color: obj.color || '#00ff00',
            emissive: obj.color || '#00ff00',
            emissiveIntensity: 0.5,
          }, 'glow');
          const gauge = new THREE.Mesh(gaugeGeometry, gaugeMaterial);
          objGroup.add(gauge);
          
          // Aiguille
          const needleGeometry = new THREE.BoxGeometry(0.03, 0.4, 0.03);
          const needleMaterial = new THREE.MeshStandardMaterial({
            color: '#ffffff',
            emissive: '#ffffff',
            emissiveIntensity: 0.8,
          });
          const needle = new THREE.Mesh(needleGeometry, needleMaterial);
          needle.position.y = 0.2;
          objGroup.add(needle);
          
          // Centre
          const centerGeometry = new THREE.SphereGeometry(0.08, 16, 16);
          const centerMaterial = new THREE.MeshStandardMaterial({
            color: '#ffffff',
            emissive: '#ffffff',
            emissiveIntensity: 1,
          });
          const center = new THREE.Mesh(centerGeometry, centerMaterial);
          objGroup.add(center);
          
          animationStateRef.current.dynamics.set(obj.id, {
            type: 'gauge',
            needle,
            value: obj.value || 70,
          });
          
        } else if (obj.type === 'node' || obj.id.includes('trader')) {
          // Nœuds de réseau
          const radius = obj.radius || 0.3;
          const nodeGeometry = new THREE.SphereGeometry(radius, 32, 32);
          const nodeMaterial = createAdvancedMaterial({
            color: obj.color || '#ffff00',
            emissive: obj.color || '#ffff00',
            emissiveIntensity: 0.8,
          }, 'glow');
          const node = new THREE.Mesh(nodeGeometry, nodeMaterial);
          objGroup.add(node);
          
          // Halo
          const haloGeometry = new THREE.SphereGeometry(radius * 1.5, 32, 32);
          const haloMaterial = new THREE.MeshBasicMaterial({
            color: obj.color || '#ffff00',
            transparent: true,
            opacity: 0.3,
            blending: THREE.AdditiveBlending,
          });
          const halo = new THREE.Mesh(haloGeometry, haloMaterial);
          objGroup.add(halo);
          
          animationStateRef.current.dynamics.set(obj.id, {
            type: 'node',
            node,
            halo,
            pulseSpeed: obj.dynamics?.pulseSpeed || 0.5,
          });
        }
        
        // Positionner et ajouter le groupe
        if (obj.position) {
          objGroup.position.set(
            obj.position.x || 0,
            obj.position.y || 0,
            obj.position.z || 0
          );
        }
        
        if (obj.scale) {
          objGroup.scale.set(
            obj.scale.x || 1,
            obj.scale.y || 1,
            obj.scale.z || 1
          );
        }
        
        objGroup.userData = {
          type: 'object',
          id: obj.id,
          label: obj.label,
          description: obj.description,
        };
        
        objGroup.traverse(child => {
          if (child.isMesh || child.isLine || child.isPoints) {
            child.userData = objGroup.userData;
          }
        });
        
        sceneState.scene.add(objGroup);
        objectMeshesRef.current[obj.id] = objGroup;
        hotspotGroupsRef.current.push(objGroup);
      });
      
      // Créer les liens entre objets
      if (config.links?.length) {
        config.links.forEach(link => {
          const fromObj = objectMeshesRef.current[link.from];
          const toObj = objectMeshesRef.current[link.to];
          
          if (fromObj && toObj) {
            const curve = new THREE.CatmullRomCurve3([
              fromObj.position.clone(),
              new THREE.Vector3(
                (fromObj.position.x + toObj.position.x) / 2,
                (fromObj.position.y + toObj.position.y) / 2 + 0.5,
                (fromObj.position.z + toObj.position.z) / 2
              ),
              toObj.position.clone(),
            ]);
            
            const tubeGeometry = new THREE.TubeGeometry(curve, 20, 0.02, 8, false);
            const tubeMaterial = new THREE.MeshBasicMaterial({
              color: link.color || '#ffffff',
              transparent: true,
              opacity: 0.3,
              blending: THREE.AdditiveBlending,
            });
            const tube = new THREE.Mesh(tubeGeometry, tubeMaterial);
            sceneState.scene.add(tube);
            linkMeshesRef.current.push(tube);
          }
        });
      }
      
    } else if (sceneTypeLower === 'forcefield' || sceneTypeLower === 'complex' || sceneTypeLower === 'research') {
      // Créer le réseau de force
      const forceNetwork = createForceFieldNetwork(config);
      forceNetwork.position.set(0, 0, 0);
      sceneState.scene.add(forceNetwork);
      objectMeshesRef.current['force_field'] = forceNetwork;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // GESTION DES INTERACTIONS SOURIS
    // ═══════════════════════════════════════════════════════════════════════════
    
    const updateMousePosition = (event) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const handleMouseMove = (event) => {
      updateMousePosition(event);
      
      raycasterRef.current.setFromCamera(mouseRef.current, sceneState.camera);
      
      const interactables = Object.values(objectMeshesRef.current);
      const intersects = raycasterRef.current.intersectObjects(interactables, true);
      
      if (intersects.length > 0) {
        const hitObject = intersects[0].object;
        const targetId = hitObject.userData?.id;
        
        if (targetId && hoveredTargetRef.current !== targetId) {
          hoveredTargetRef.current = targetId;
          setHoveredTarget?.(targetId);
          container.style.cursor = 'pointer';
          
          // Effet de surbrillance
          objectMeshesRef.current[targetId]?.traverse(child => {
            if (child.material && child.material.emissiveIntensity !== undefined) {
              child.material.emissiveIntensity = 1.5;
            }
          });
        }
      } else {
        if (hoveredTargetRef.current !== null) {
          // Restaurer l'intensité d'émission
          objectMeshesRef.current[hoveredTargetRef.current]?.traverse(child => {
            if (child.material && child.material.emissiveIntensity !== undefined) {
              child.material.emissiveIntensity = 0.5;
            }
          });
          
          hoveredTargetRef.current = null;
          setHoveredTarget?.(null);
          container.style.cursor = 'grab';
        }
      }
    };

    const handleClick = (event) => {
      updateMousePosition(event);
      
      raycasterRef.current.setFromCamera(mouseRef.current, sceneState.camera);
      
      const interactables = Object.values(objectMeshesRef.current);
      const intersects = raycasterRef.current.intersectObjects(interactables, true);
      
      if (intersects.length > 0) {
        const hitObject = intersects[0].object;
        const objId = hitObject.userData?.id;
        
        if (objId && onHotspotClick) {
          const interaction = config.interactions?.find(i => i.objectId === objId);
          if (interaction) {
            onHotspotClick(interaction);
          }
        }
      }
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('click', handleClick);
    container.addEventListener('mouseleave', () => {
      hoveredTargetRef.current = null;
      setHoveredTarget?.(null);
    });

    // ═══════════════════════════════════════════════════════════════════════════
    // BOUCLE D'ANIMATION
    // ═══════════════════════════════════════════════════════════════════════════
    
    const clock = new THREE.Clock();
    let frame = 0;
    
    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      frame++;
      const time = clock.getElapsedTime();
      const delta = clock.getDelta();
      
      animationStateRef.current.time = time;
      
      // Mise à jour des contrôles
      sceneState.controls.update();
      
      // Mise à jour des animations dynamiques
      animationStateRef.current.dynamics.forEach((dyn, objId) => {
        if (dyn.type === 'heart') {
          // Animation de battement cardiaque
          const beat = Math.sin(time * dyn.beatSpeed * 2 * Math.PI);
          const scale = dyn.baseScale + (beat > 0 ? beat : 0) * dyn.beatAmplitude;
          dyn.heart.scale.set(scale, scale, scale);
          
          // Pulsation de l'émission
          if (dyn.heart.material.emissiveIntensity !== undefined) {
            dyn.heart.material.emissiveIntensity = 0.5 + (beat > 0 ? beat : 0) * 1.5;
          }
          
        } else if (dyn.type === 'trading') {
          // Mise à jour des bougies
          if (frame % 30 === 0) {
            dyn.candles.forEach(candle => {
              const change = (Math.random() - 0.5) * 0.2;
              candle.close += change;
              candle.high = Math.max(candle.high, candle.close + Math.random() * 0.1);
              candle.low = Math.min(candle.low, candle.close - Math.random() * 0.1);
              
              // Mettre à jour la géométrie
              const newHeight = Math.abs(candle.close - candle.open);
              candle.body.scale.y = newHeight;
              candle.body.position.y = (candle.open + candle.close) / 2;
              
              // Changer la couleur selon la direction
              const isGreen = candle.close > candle.open;
              candle.body.material.color.set(isGreen ? '#00ff00' : '#ff0000');
              candle.body.material.emissive.set(isGreen ? '#00ff00' : '#ff0000');
            });
          }
          
        } else if (dyn.type === 'network') {
          // Animation du réseau
          dyn.nodes.forEach((node, index) => {
            const offset = Math.sin(time * 0.5 + index * 0.3) * 0.2;
            node.mesh.position.y = node.originalPos.y + offset;
            node.halo.material.opacity = 0.3 + Math.sin(time * 2 + index) * 0.2;
          });
          
        } else if (dyn.type === 'particles') {
          // Animation des particules
          for (let i = 0; i < dyn.velocities.length; i++) {
            const vel = dyn.velocities[i];
            vel.angle += vel.speed * 0.01;
            
            dyn.positions[i * 3] = Math.cos(vel.angle) * vel.radius;
            dyn.positions[i * 3 + 1] = Math.sin(time + vel.yOffset) * 0.5;
            dyn.positions[i * 3 + 2] = Math.sin(vel.angle) * vel.radius;
          }
          
          dyn.geometry.attributes.position.needsUpdate = true;
          
        } else if (dyn.type === 'node') {
          // Animation des nœuds
          const pulse = Math.sin(time * dyn.pulseSpeed) * 0.1;
          dyn.node.scale.set(1 + pulse, 1 + pulse, 1 + pulse);
          dyn.halo.scale.set(1.5 + pulse * 2, 1.5 + pulse * 2, 1.5 + pulse * 2);
          dyn.halo.material.opacity = 0.3 + pulse * 0.2;
          
        } else if (dyn.type === 'gauge') {
          // Animation de la jauge
          const value = dyn.value / 100;
          dyn.needle.rotation.z = -Math.PI * 0.75 + value * Math.PI * 1.5;
        }
      });
      
      // Mise à jour du post-processing
      if (postProcessingRef.current) {
        postProcessingRef.current.updateTime(time);
      }
      
      // Rendu
      if (sceneState.composer) {
        sceneState.composer.render();
      } else {
        sceneState.renderer.render(sceneState.scene, sceneState.camera);
      }
    };
    
    animate();

    // ═══════════════════════════════════════════════════════════════════════════
    // NETTOYAGE
    // ═══════════════════════════════════════════════════════════════════════════
    
    return () => {
      cancelAnimationFrame(rafRef.current);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('click', handleClick);
      
      cameraRef.current = sceneState.getCameraState();
      
      // Nettoyer les liens
      linkMeshesRef.current.forEach(link => {
        if (link.geometry) link.geometry.dispose();
        if (link.material) link.material.dispose();
      });
      linkMeshesRef.current = [];
      
      // Nettoyer les objets
      Object.values(objectMeshesRef.current).forEach(group => {
        group.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      });
      
      sceneState.dispose();
      threeRef.current = null;
      postProcessingRef.current = null;
    };
    
  }, [config, sceneType, layerOpacities]);

  return (
    <div 
      ref={containerRef} 
      className="w-full h-full" 
      style={{ cursor: 'grab' }}
      onMouseDown={(e) => { e.currentTarget.style.cursor = 'grabbing'; }}
      onMouseUp={(e) => { e.currentTarget.style.cursor = 'grab'; }}
    />
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export const Visualization3DView = () => {
  const { caseId, slug } = useParams();
  const activeId = caseId || slug;

  const { payload, status, latency, lastUpdate, errorMsg } = useInferenceEngine(activeId);

  const [hoveredTarget, setHoveredTarget] = useState(null);
  const [selectedInter, setSelectedInter] = useState(null);
  const [layerOpacities, setLayerOpacities] = useState({});
  const [showLeftPanel, setShowLeftPanel] = useState(true);

  
  const config = useMemo(() => extractVisualization(payload, 0, activeId) || {}, [payload, activeId]);
  const engineState = useMemo(() => extractState(payload), [payload]);
  const caseData = useMemo(() => extractCase(payload), [payload]);
  const parameters = useMemo(() => engineState?.parameters || {}, [engineState]);
  
  const currentStepIndex = engineState?.currentStepIndex ?? 0;
  const currentStep = caseData?.steps?.[currentStepIndex] || null;
  const totalSteps = caseData?.steps?.length || 0;
  const tensionLevel = currentStep?.narrativeArc?.tensionLevel ?? 2;

  const sceneType = config.type;
  const hasLayers = (config.layers?.length ?? 0) > 0;
  const hasObjects = (config.objects?.length ?? 0) > 0;
  
  const handleHotspot = useCallback(
    inter => {
      const layer = config.layers?.find(l => l.id === (inter.objectId || inter.id));
      const obj = config.objects?.find(o => o.id === (inter.objectId || inter.id));
      setSelectedInter({ inter, layer, obj });
    },
    [config]
  );

  const profile = useMemo(() => getRenderingProfile(config.type), [config.type]);
  const C = useMemo(() => getDesignSystem(profile).colors, [profile]);
  const MONO = useMemo(() => getDesignSystem(profile).fonts.mono, [profile]);
  const DISPLAY = DESIGN_SYSTEM.fonts.display;
  const BODY = DESIGN_SYSTEM.fonts.body;
  

  const tensionColors = [C.textMuted, C.green, C.cyan, C.amber, C.amber, C.red];
  const tensionLabels = ['—', 'STABLE', 'ACTIF', 'TENDU', 'CRITIQUE', 'RUPTURE'];
  const tColor = tensionColors[Math.min(tensionLevel, 5)];

  

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden" style={{ background: C.bg }}>
      {/* HEADER */}
      <header
        style={{
          height: 44,
          flexShrink: 0,
          background: C.bgPanel,
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
        }}
      >
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.textAccent, letterSpacing: '0.2em' }}>
          {profile.name.toUpperCase()} · {profile.audience.toUpperCase()}

        
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.08em' }}>
              TENSION
            </span>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  style={{
                    width: 4,
                    height: 4 + i * 2,
                    background: i <= tensionLevel ? tColor : C.textMuted + '20',
                    borderRadius: 1,
                    boxShadow: i <= tensionLevel ? `0 0 4px ${tColor}60` : 'none',
                  }}
                />
              ))}
            </div>
            <span style={{ fontFamily: MONO, fontSize: 8, color: tColor, letterSpacing: '0.1em' }}>
              {tensionLabels[Math.min(tensionLevel, 5)]}
            </span>
          </div>

          <div style={{ width: 1, height: 14, background: C.border }} />

          <SignalBar status={status} latency={latency} />
        </div>
        
      </header>

      {/* BODY */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* LEFT PANEL */}
        <motion.aside
                  animate={{ width: showLeftPanel ? 216 : 36 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    background: C.bgPanel,
                    borderRight: `1px solid ${C.border}`,
                    overflow: 'hidden',
                  }}
                >
                  <button
                    onClick={() => setShowLeftPanel(v => !v)}
                    style={{
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 10px',
                      gap: 6,
                      cursor: 'pointer',
                      borderBottom: `1px solid ${C.border}`,
                      background: 'transparent',
                      color: C.textMuted,
                      flexShrink: 0,
                    }}
                  >
                    <Layers size={12} color={C.textAccent} />
                    <AnimatePresence>
                      {showLeftPanel && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          style={{
                            fontFamily: MONO,
                            fontSize: 8,
                            color: C.textSecond,
                            letterSpacing: '0.1em',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {hasLayers ? 'COUCHES' : 'ACTEURS'}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
        
                  {showLeftPanel && (
                    <div style={{ flex: 1, overflowY: 'auto', padding: 8, scrollbarWidth: 'none' }}>
                      {hasLayers && (
                        <LayersPanel
                          layers={config.layers}
                          layerOpacities={layerOpacities}
                          setLayerOpacities={setLayerOpacities}
                        />
                      )}
                      {!hasLayers && hasObjects && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {config.objects.map(obj => (
                            <button
                              key={obj.id}
                              onClick={() =>
                                handleHotspot({
                                  id: obj.id,
                                  objectId: obj.id,
                                  label: obj.label,
                                  description: obj.description,
                                })
                              }
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '7px 10px',
                                borderRadius: 4,
                                cursor: 'pointer',
                                textAlign: 'left',
                                background: hoveredTarget === obj.id ? obj.color + '15' : C.bgInset,
                                border: `1px solid ${hoveredTarget === obj.id ? obj.color + '40' : C.border}`,
                                transition: 'all 0.15s',
                              }}
                            >
                              <div
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  background: obj.color,
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ fontFamily: MONO, fontSize: 10, color: C.textPrimary }}>
                                {obj.label || obj.id}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
        
                  {showLeftPanel && (
                    <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
                      <div style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.1em', marginBottom: 3 }}>
                        CIBLE
                      </div>
                      <div style={{ fontFamily: DISPLAY, fontSize: 12, color: C.textAccent, fontWeight: 600 }}>
                        {config.targetLabel || config.target || '—'}
                      </div>
                      {config.scaleUnit && (
                        <div style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, marginTop: 2 }}>
                          {config.scaleUnit}
                        </div>
                      )}
                    </div>
                  )}
        </motion.aside>
                
        <main style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <UnifiedSceneRenderer
            config={config}
            parameters={parameters}
            state={engineState}
            hoveredTarget={hoveredTarget}
            setHoveredTarget={setHoveredTarget}
            layerOpacities={layerOpacities}
            onHotspotClick={handleHotspot}
            sceneType={config.type}
          />
        <div style={{ position: 'absolute', top: 12, left: 12, maxWidth: 360, zIndex: 20, pointerEvents: 'none' }}>
          <AnimatePresence mode="wait">
              {currentStep && (
                <motion.div
                  key={currentStepIndex}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                >
                <StepPanel step={currentStep} stepIndex={currentStepIndex} totalSteps={totalSteps} />
                </motion.div>
              )}
          </AnimatePresence>
        </div>
        
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            left: 12,
            zIndex: 10,
            fontFamily: MONO,
            fontSize: 8,
            color: C.textMuted + '50',
            letterSpacing: '0.12em',
            pointerEvents: 'none',
          }}
        >
            DRAG · SCROLL · CLICK-DROIT
        </div>
        
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            fontFamily: MONO,
            fontSize: 10,
            color: C.textMuted + '18',
            letterSpacing: '0.22em',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
            LABORATOIRE · ANALYSES · RECHERCHE
        </div>
        
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            right: 12,
            zIndex: 10,
            fontFamily: MONO,
            fontSize: 7,
            color: C.textMuted + '40',
            letterSpacing: '0.08em',
            pointerEvents: 'none',
          }}
        >
          ENGINE v1.0
        </div>
        </main>
        
        {/* RIGHT PANEL */}
        <aside
          style={{
            width: 256,
            flexShrink: 0,
            background: C.bgPanel,
            borderLeft: `1px solid ${C.border}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            scrollbarWidth: 'none',
          }}
        >
        {/* Inference metrics */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Cpu size={9} color={C.textMuted} />
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.12em' }}>
              MOTEUR D'INFÉRENCE
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[
              { label: 'TYPE', value: sceneType?.toUpperCase() || '—', color: C.cyan },
              { label: 'ÉTAPE', value: `${currentStepIndex + 1} / ${totalSteps || '—'}`, color: C.textPrimary },
              {
                label: 'LATENCE',
                value: latency ? `${latency} ms` : '—',
                color: latency && latency < 50 ? C.green : C.amber,
              },
              { label: 'LAYERS', value: String(hasLayers ? config.layers.length : 0), color: C.textPrimary },
              { label: 'OBJETS', value: String(hasObjects ? config.objects.length : 0), color: C.textPrimary },
            ].map(row => (
              <div
                key={row.label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 8px',
                  background: C.bgInset,
                  borderRadius: 3,
                }}
              >
                <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.08em' }}>
                  {row.label}
                </span>
                <span style={{ fontFamily: MONO, fontSize: 9, color: row.color, fontWeight: 500 }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        <div style={{ height: 1, background: C.border }} />
                {/* Parameters */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Activity size={9} color={C.textMuted} />
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.12em' }}>
              PARAMÈTRES
            </span>
          </div>
          <ParameterMonitor parameters={parameters} />
        </div>
        <div style={{ height: 1, background: C.border }} />
        {/* Inspection */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Microscope size={9} color={C.textMuted} />
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.textMuted, letterSpacing: '0.12em' }}>
              INSPECTION
            </span>
          </div>
          <AnimatePresence mode="wait">
            {selectedInter ? (
              <InfoCard
                key={selectedInter.inter.id}
                inter={selectedInter.inter}
                layer={selectedInter.layer}
                obj={selectedInter.obj}
                parameters={parameters}
                onClose={() => setSelectedInter(null)}
              />
            ) : (
              <motion.div
                key="empty-inspection"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  padding: '20px 12px',
                  textAlign: 'center',
                  background: C.bgInset,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                }}
              >
                <motion.div animate={{ opacity: [0.15, 0.45, 0.15] }} transition={{ duration: 3, repeat: Infinity }}>
                  <Microscope size={18} color={C.textMuted} style={{ margin: '0 auto 8px' }} />
                </motion.div>
                <p style={{ fontFamily: MONO, fontSize: 9, color: C.textMuted, lineHeight: 1.7 }}>
                  SURVOLEZ UN POINT<br />D'INTÉRÊT
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
        
        {/* Footer */}
        {caseData?.initialState?.theory && (
          <div style={{ padding: '10px 12px', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
            <div className="flex items-center gap-1.5 mb-2">
              <BookOpen size={8} color={C.textMuted} />
              <span style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted, letterSpacing: '0.12em' }}>
                CADRE THÉORIQUE
              </span>
            </div>
            <p
              style={{
                fontFamily: BODY,
                fontSize: 11,
                color: C.textMuted,
                lineHeight: 1.65,
                borderLeft: `2px solid ${C.cyanDim}`,
                paddingLeft: 8,
                margin: 0,
              }}
            >
              {caseData.initialState.theory.quote?.slice(0, 100)}…
            </p>
            <p style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted + '70', marginTop: 4 }}>
              {caseData.initialState.theory.author} · {caseData.initialState.theory.year}
            </p>
          </div>
        )}
        </aside>
      </div>
        
      {/* FOOTER */}
      <footer
        style={{
          height: 22,
          flexShrink: 0,
          background: C.bgPanel,
          borderTop: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 14px',
        }}
      >
        <span style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted + '50', letterSpacing: '0.1em' }}>
          PESACORE Architect · ENGINE v1.0 · {sceneType?.toUpperCase() || 'IDLE'} · PHYSIOLOGIE INTÉGRATIVE
        </span>
        <span style={{ fontFamily: MONO, fontSize: 7, color: C.textMuted + '50' }}>
          {new Date().toISOString().split('T')[0]}
        </span>
      </footer>
    </div>
  );
};

export default Visualization3DView;